// Builds the token-substitution context for a contract.
// One source of truth used by every contract render path.
//
// Inputs are intentionally loose so this works at any sign stage:
//   - pre-sign in BookingEngine (draft booking object)
//   - check-in (rental object + check-in form)
//   - lease creation (lease draft)
//   - post-sign viewer (saved rental + saved snapshot)

function formatDate(d) {
  if (!d) return '';
  if (typeof d === 'string') {
    // Already formatted (e.g., "10 Jan 2026") → keep as-is
    if (/[A-Za-z]/.test(d)) return d;
    // ISO → format
    const parsed = new Date(d);
    if (isNaN(parsed)) return d;
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (d instanceof Date) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return String(d);
}

function fmtMoney(n) {
  if (n == null || n === '') return '';
  const v = parseFloat(n);
  if (isNaN(v)) return String(n);
  return v.toFixed(2);
}

function fmtInt(n) {
  if (n == null || n === '') return '';
  const v = parseInt(n);
  if (isNaN(v)) return String(n);
  return v.toLocaleString();
}

function joinList(arr) {
  if (!arr) return '';
  if (typeof arr === 'string') return arr;
  if (!Array.isArray(arr)) return '';
  return arr.map(x => typeof x === 'string' ? x : (x?.name || x?.label || '')).filter(Boolean).join(', ');
}

/**
 * Build a token → value map.
 *
 * @param {object} args
 * @param {object} [args.rental]          The rental / lease record (or sign draft)
 * @param {object} [args.vehicle]         The vehicle record
 * @param {object} [args.customer]        The customer record (optional — falls back to rental fields)
 * @param {object} [args.company]         companySettings
 * @param {string} [args.signatureDataUrl] base64 signature PNG (for post-sign render)
 * @param {string} [args.signedAt]        ISO timestamp of sign
 */
export function buildContractContext({ rental = {}, vehicle = {}, customer = {}, company = {}, signatureDataUrl = '', signedAt = '' } = {}) {
  const r = rental, v = vehicle, c = customer, co = company;

  const sigImg = signatureDataUrl
    || r.signatureDataUrl
    || r.signData
    || r.presignData
    || r.agreementSaved?.signData
    || '';
  const sigImgHtml = sigImg
    ? `<img src="${sigImg}" alt="Signature" style="max-height:70px;background:#fff;border:1px solid #ddd;border-radius:4px;padding:4px;display:inline-block;" />`
    : `<div style="display:inline-block;border:2px dashed #ccc;background:#fafafa;color:#999;padding:14px 24px;font-style:italic;font-size:12px;letter-spacing:.04em;border-radius:6px;text-transform:uppercase;">✕ Not yet signed</div>`;

  const signedAtIso = signedAt || r.signedAt || r.presignedAt || r.agreementSaved?.signedAt || '';
  const signedAtFmt = signedAtIso
    ? (signedAtIso.includes('T') ? new Date(signedAtIso).toLocaleString('en-GB') : signedAtIso)
    : (r.agreementSaved?.signedAtFormatted || '');

  const logoHtml = co.logoDataUrl
    ? `<img src="${co.logoDataUrl}" alt="Logo" style="max-height:60px;object-fit:contain;" />`
    : (co.name || '');

  const customerName = r.customer
    || [c.fname, c.lname].filter(Boolean).join(' ')
    || '';

  return {
    // Lessee
    '{{lessee_name}}':       customerName,
    '{{lessee_company}}':    r.customerCompany || c.company || '',
    '{{lessee_address}}':    r.address || c.address || '',
    '{{lessee_passport}}':   r.passport || c.passport || '',
    '{{lessee_licence}}':    r.licence || c.licence || '',
    '{{lessee_licence_expiry}}': r.licExpiry || c.licExpiry || '',
    '{{lessee_email}}':      r.email || c.email || '',
    '{{lessee_phone}}':      r.phone || c.phone || '',
    '{{lessee_vat}}':        r.vatNumber || c.vatNumber || '',
    '{{lessee_nationality}}': r.nationality || c.nationality || '',
    '{{lessee_dob}}':        r.dob || c.dob || '',

    // Vehicle
    '{{vehicle_name}}':         v.name || r.vid || '',
    '{{vehicle_plate}}':        v.plate || '',
    '{{vehicle_year}}':         v.year || '',
    '{{vehicle_vin}}':          v.vin || '',
    '{{vehicle_colour}}':       v.colour || v.color || '',
    '{{vehicle_mileage}}':      fmtInt(v.odometer || r.startKm || ''),
    '{{vehicle_fuel}}':         v.fuelType || v.fuel || '',
    '{{vehicle_transmission}}': v.transmission || '',

    // Rental / Lease
    '{{contract_id}}':         r.id || '',
    '{{start_date}}':          r.startLabel || formatDate(r.start) || '',
    '{{end_date}}':            r.endLabel || formatDate(r.end) || '',
    '{{start_time}}':          r.t1 || '',
    '{{end_time}}':            r.t2 || '',
    '{{pickup_location}}':     r.pickupLocName || '',
    '{{return_location}}':     r.returnLocName || r.pickupLocName || '',
    '{{term_months}}':         r.termMonths || '',
    '{{duration_days}}':       r.calSpan || r.days || '',
    '{{monthly_rate}}':        r.monthlyRate ? fmtMoney(r.monthlyRate) : '',
    '{{daily_rate}}':          r.rate ? fmtMoney(r.rate) : '',
    '{{annual_mileage_cap}}':  r.annualMileageCap ? fmtInt(r.annualMileageCap) : '',
    '{{deposit}}':             r.deposit != null ? fmtMoney(r.deposit) : '',
    '{{deposit_method}}':      r.depositMethod || '',
    '{{total_contract}}':      r.agreedPrice != null ? fmtMoney(r.agreedPrice) : '',
    '{{included_services}}':   r.includedServices || '',
    '{{insurance_name}}':      r.insurance || r.agreementSaved?.insuranceName || '',
    '{{insurance_excess}}':    r.insuranceExcess != null ? String(r.insuranceExcess) : (r.agreementSaved?.insuranceExcess != null ? String(r.agreementSaved.insuranceExcess) : ''),
    '{{extras}}':              joinList(r.extras),

    // Lessor
    '{{lessor_name}}':    co.name || '',
    '{{lessor_address}}': co.address || '',
    '{{lessor_phone}}':   co.phone || '',
    '{{lessor_email}}':   co.email || '',
    '{{lessor_vat}}':     co.vatNum || '',
    '{{lessor_regnum}}':  co.regNum || '',
    '{{lessor_logo}}':    logoHtml,

    // Signature
    '{{signature}}':   sigImgHtml,
    '{{signed_date}}': signedAtFmt,
    '{{today}}':       new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
  };
}

// Sample context for the template editor's live preview.
export function sampleContractContext(company = {}) {
  return buildContractContext({
    rental: {
      id: 'CNT-PREVIEW',
      customer: 'John Sample',
      customerCompany: '',
      passport: 'AB123456',
      licence: 'CY-DL-987654',
      licExpiry: '31 Dec 2030',
      email: 'john@example.com',
      phone: '+357 99 000000',
      address: 'Sample Street 12, Paphos',
      nationality: 'Cypriot',
      vatNumber: '',
      startLabel: '1 Jan 2026',
      endLabel: '31 Dec 2026',
      t1: '09:00', t2: '09:00',
      pickupLocName: 'Paphos Office',
      returnLocName: 'Paphos Office',
      termMonths: 12,
      calSpan: 365,
      monthlyRate: 450,
      rate: 25,
      annualMileageCap: 25000,
      deposit: 500,
      depositMethod: 'Bank guarantee',
      agreedPrice: 5400,
      includedServices: 'Maintenance, insurance, roadside assistance',
      insurance: 'Comprehensive',
      insuranceExcess: 300,
      extras: ['Baby seat', 'GPS'],
    },
    vehicle: {
      name: 'TOYOTA YARIS – ABC123',
      plate: 'ABC123',
      year: 2024,
      vin: 'JT123456789012345',
      colour: 'Silver',
      odometer: 12500,
      fuelType: 'Petrol',
      transmission: 'Automatic',
    },
    company,
    signedAt: new Date().toISOString(),
  });
}
